/**
 * @file PlanUsageChart.js
 * @description Standalone plan usage breakdown component for the Revisual backoffice.
 *
 * Renders a 12-month bar chart alongside a sidebar showing the current
 * month's usage, progress toward quota, and a smart plan recommendation
 * derived from historical usage patterns.
 *
 * Fetches its own data — no props drilling required beyond plan metadata.
 * Completely independent from the time-series windowed chart.
 *
 * Styled on the app's Untitled UI theme (`apps/admin/theme/untitled`) —
 * this module is no longer Bootstrap-agnostic, see README.
 *
 * @module PlanUsageChart
 */

import { useMemo, useCallback } from "react";
import classNames from "classnames";
import { Chart } from "react-google-charts";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import { computeRecommendation } from "./recommendationEngine";
import PlanUsageChartSkeleton from "./PlanUsageChartSkeleton";
import Card from "../../../apps/admin/theme/untitled/Card/Card";
import Button from "../../../apps/admin/theme/untitled/Button/Button";
import "./PlanUsageChart.scss";
import CHART_COLORS from "./chartColors";
function getStartOfNextMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}
const formatter_mmdy = new Intl.DateTimeFormat(
  window.navigator.language || "en-US",
  {
    year: "numeric",
    month: "short",
    day: "numeric",
  }
);

// ─── Reco config ──────────────────────────────────────────────────────────────

/**
 * Human-readable titles for each recommendation type.
 * @type {Record<string, string>}
 */
const RECO_TITLES = {
  over_quota: "Quota exceeded",
  upgrade: "Consider upgrading",
  downgrade: "Room to save",
  on_track: "Looking good",
  building: "Building history",
};

/**
 * Icon characters for each recommendation type.
 * @type {Record<string, string>}
 */
const RECO_ICONS = {
  over_quota: "↑",
  upgrade: "↑",
  downgrade: "↓",
  on_track: "✓",
  building: "◌",
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────

/**
 * Returns `value` as a percentage of `total`, clamped to 100.
 *
 * @param {number} value
 * @param {number} total
 * @returns {number}
 */
const pct = (value, total) => {
  if (!total || total === -1) return 0;
  return Math.min(100, Math.round((value / total) * 100));
};

/**
 * Formats a view count as a human-readable string.
 * Numbers >= 1000 are shown as e.g. "1.2k".
 *
 * @param {number|null|undefined} n
 * @returns {string}
 */
const fmt = (n) => {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
};

const fmtQuota = (quota) => (quota === -1 ? "Unlimited" : fmt(quota));

/**
 * Formats a price as a USD string. Omits decimals for whole numbers.
 *
 * @param {number|null|undefined} n
 * @returns {string}
 */
const fmtPrice = (n) => {
  if (n == null) return "";
  return `$${n % 1 === 0 ? n : n.toFixed(2)}`;
};

/**
 * Builds the two-dimensional data array consumed by react-google-charts.
 * Applies bar colours and HTML tooltip markup per row.
 *
 * @param {Array<{billing_month: string, widget_views: number}>|null} rows
 * @param {number} quota  Monthly view quota for the current plan.
 * @returns {Array|null}  null when rows is empty or missing.
 */
const buildChartData = (rows, quota) => {
  if (!rows || rows.length === 0) return null;
  const thisMonth = dayjs().format("YYYY-MM");
  const hasUnlimitedQuota = quota === -1;

  return [
    [
      "Month",
      "Views",
      { role: "style" },
      { role: "tooltip", type: "string", p: { html: true } },
    ],
    ...rows.map((row) => {
      const isCurrent = row.billing_month === thisMonth;
      const views = Number(row.widget_views) || 0;
      const overQuota = !hasUnlimitedQuota && quota && views > quota;
      const barColor = overQuota
        ? CHART_COLORS.error500
        : isCurrent
        ? CHART_COLORS.brand600
        : CHART_COLORS.brand500;
      const opacity = !isCurrent && !overQuota ? "opacity: 0.6;" : "";

      const d = dayjs(row.billing_month + "-01");
      const label =
        d.month() === 0
          ? `${d.format("MMM")}\n${d.format("YYYY")}`
          : d.format("MMM");

      const tooltip = `
        <div style="padding:10px 14px;font-family:'Inter',system-ui,sans-serif;min-width:130px">
          <div style="font-size:11px;color:${
            CHART_COLORS.grey500
          };margin-bottom:4px;text-transform:uppercase;letter-spacing:0.04em">
            ${d.format("MMMM YYYY")}
          </div>
          <div style="font-size:15px;font-weight:700;color:${
            CHART_COLORS.grey800
          }">
            ${views.toLocaleString()} views
          </div>
          ${
            quota && !hasUnlimitedQuota
              ? `<div style="font-size:11px;color:${barColor};margin-top:3px;font-weight:600">
                ${pct(views, quota)}% of quota
               </div>`
              : ""
          }
        </div>`;

      return [label, views, `color: ${barColor}; ${opacity}`, tooltip];
    }),
  ];
};

/**
 * Builds the Google Charts options object for the column chart.
 *
 * @param {number} maxVal  Highest value visible in the chart (used to set the y-axis ceiling).
 * @returns {object}
 */
const buildChartOptions = (maxVal) => ({
  bar: { groupWidth: "64%" },
  legend: { position: "none" },
  tooltip: { isHtml: true, trigger: "focus" },
  hAxis: {
    textStyle: { fontSize: 11, color: CHART_COLORS.grey500 },
    gridlines: { color: "transparent" },
    baselineColor: "transparent",
  },
  vAxis: {
    minValue: 0,
    textStyle: { fontSize: 11, color: CHART_COLORS.grey400 },
    gridlines: { color: CHART_COLORS.grey100, count: 4 },
    baselineColor: CHART_COLORS.grey200,
    viewWindow: { min: 0, max: maxVal * 1.2 },
  },
  chartArea: { left: 52, right: 4, top: 8, bottom: 36 },
  backgroundColor: "transparent",
});

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Thin horizontal rule between sidebar sections.
 *
 * @returns {JSX.Element}
 */
const Divider = () => <div className="r-usage-divider" />;

/**
 * Small circular icon that shows a tooltip hint on hover.
 *
 * @param {object} props
 * @param {string} props.hint  Tooltip text shown on hover.
 * @returns {JSX.Element}
 */
const HintIcon = ({ hint }) => (
  <span className="r-usage-hint" title={hint}>
    ?
  </span>
);

/**
 * A labelled metric row with a coloured dot, label, optional hint, and value.
 * Used for the current/projected/quota breakdown list in the sidebar.
 *
 * @param {object}      props
 * @param {'current'|'projected'|'included'} props.tone  Selects the dot colour.
 * @param {string}      props.label  Row label text.
 * @param {string}      props.value  Formatted value string.
 * @param {string}      [props.hint] Optional tooltip text for the hint icon.
 * @returns {JSX.Element}
 */
const DotRow = ({ tone, label, value, hint }) => (
  <div className="r-usage-row">
    <span className="r-usage-row-start">
      <span className={classNames("r-usage-dot", `r-usage-dot--${tone}`)} />
      <span className="r-usage-dot-label">{label}</span>
      {hint && <HintIcon hint={hint} />}
    </span>
    <span className="r-usage-dot-value">{value}</span>
  </div>
);

/**
 * A simple two-column label/value row.
 * Used for the billing reset date in the sidebar.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.value
 * @returns {JSX.Element}
 */
const StatRow = ({ label, value }) => (
  <div className="r-usage-row">
    <span className="r-usage-stat-label">{label}</span>
    <span className="r-usage-stat-value">{value}</span>
  </div>
);

/**
 * Stacked progress bar showing used views and projected views against quota.
 *
 * @param {object}  props
 * @param {number}  props.usedPct   Used views as a percentage of quota (0–100).
 * @param {number}  props.projPct   Projected views as a percentage of quota (0–100).
 * @param {boolean} props.overQuota When true, renders the used segment in error red.
 * @returns {JSX.Element}
 */
const ProgressBar = ({ usedPct, projPct, overQuota }) => {
  const capUsed = Math.min(usedPct, 100);
  const capProj = Math.min(projPct - usedPct, 100 - capUsed);

  return (
    <div className="r-usage-progress">
      <div
        className={classNames("r-usage-progress-used", {
          "r-usage-progress-used--over": overQuota,
          "r-usage-progress-used--has-proj": capProj > 0,
        })}
        style={{ width: `${capUsed}%` }}
      />
      {capProj > 0 && (
        <div
          className="r-usage-progress-projected"
          style={{ width: `${capProj}%` }}
        />
      )}
    </div>
  );
};

/**
 * Contextual recommendation panel shown at the bottom of the sidebar.
 * Displays a title, reason text, target plan card, and upgrade/downgrade CTA.
 * Renders nothing when `rec` is null.
 *
 * @param {object}      props
 * @param {import('./recommendationEngine').Recommendation|null} props.rec
 *   Recommendation object from {@link computeRecommendation}.
 * @param {string}      props.billingPath  URL for the upgrade/downgrade CTA link.
 * @returns {JSX.Element|null}
 */
const RecommendationPanel = ({ rec, billingPath }) => {
  const history = useHistory();
  const goToBilling = useCallback(
    () => history.push(billingPath),
    [history, billingPath]
  );

  if (!rec) return null;

  const { type } = rec;
  const showCta =
    ["upgrade", "over_quota", "downgrade"].includes(type) && rec.targetPlan;

  const ctaLabel =
    type === "downgrade"
      ? `Switch to ${rec.targetPlan?.name}`
      : `Upgrade to ${rec.targetPlan?.name}`;

  const ctaSubtext =
    type === "downgrade" && rec.savings
      ? `Save ${fmtPrice(rec.savings)}/mo`
      : rec.extraCost
      ? `+${fmtPrice(rec.extraCost)}/mo`
      : null;

  return (
    <div className={classNames("r-usage-reco", `r-usage-reco--${type}`)}>
      {/* Title row */}
      <div className="r-usage-reco-titlerow">
        <div
          className={classNames(
            "r-usage-reco-icon",
            `r-usage-reco-icon--${type}`
          )}
        >
          {RECO_ICONS[type]}
        </div>
        <span
          className={classNames(
            "r-usage-reco-title",
            `r-usage-reco-title--${type}`
          )}
        >
          {RECO_TITLES[type]}
        </span>
        {rec.confidence === "low" && (
          <span className="r-usage-reco-confidence">limited data</span>
        )}
      </div>

      {/* Reason */}
      <p className="r-usage-reco-reason">{rec.reason}</p>

      {/* Target plan card */}
      {rec.targetPlan && (
        <div
          className={classNames(
            "r-usage-plan-card",
            `r-usage-plan-card--${type}`
          )}
        >
          <div>
            <div className="r-usage-plan-name">{rec.targetPlan.name}</div>
            <div className="r-usage-plan-quota">
              {fmtQuota(rec.targetPlan.quota)} views/mo
            </div>
          </div>
          <div className="r-usage-plan-price-block">
            <div className="r-usage-plan-price">
              {fmtPrice(rec.targetPlan.price)}
              <span className="r-usage-plan-price-unit">/mo</span>
            </div>
            {ctaSubtext && (
              <div
                className={classNames("r-usage-plan-delta", {
                  "r-usage-plan-delta--save": type === "downgrade",
                  "r-usage-plan-delta--upgrade": type !== "downgrade",
                })}
              >
                {ctaSubtext}
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTA */}
      {showCta && (
        <Button
          hierarchy={"tertiary"}
          fullWidth
          onClick={goToBilling}
          className={classNames("r-usage-reco-cta-btn", `r-usage-reco-cta-btn--${type}`)}
        >
          {ctaLabel} →
        </Button>
      )}
    </div>
  );
};

/**
 * Right-hand sidebar showing current month usage, progress bar,
 * breakdown rows, billing reset date, and recommendation panel.
 *
 * @param {object}      props
 * @param {number}      props.currentViews  Views recorded so far this month.
 * @param {number}      props.quota         Monthly view quota for the current plan.
 * @param {number}      props.projected     Estimated end-of-month views.
 * @param {string|null} props.billingDate   ISO date of next quota reset.
 * @returns {JSX.Element}
 */
const Sidebar = ({ currentViews, quota, projected, billingDate }) => {
  const hasUnlimitedQuota = quota === -1;
  const usedPct = pct(currentViews, quota);
  const projPct = pct(projected, quota);
  const overQuota = !hasUnlimitedQuota && currentViews > quota;

  const statusTone = hasUnlimitedQuota
    ? "success"
    : overQuota
    ? "error"
    : projPct >= 95
    ? "warning"
    : projPct >= 75
    ? "warning-soft"
    : "success";

  const statusLabel = hasUnlimitedQuota
    ? "Unlimited"
    : overQuota
    ? "Over quota"
    : projPct >= 95
    ? "Near limit"
    : projPct >= 75
    ? "High usage"
    : "On track";

  return (
    <div className="r-usage-sidebar">
      {/* Month + big number */}
      <div className="r-usage-month-block">
        <div className="r-usage-month-label">{dayjs().format("MMMM YYYY")}</div>
        <div className="r-usage-totals-row">
          <span className="r-usage-current-views">{fmt(currentViews)}</span>
          <span className="r-usage-quota-total">{fmtQuota(quota)}</span>
        </div>
        {!hasUnlimitedQuota && (
          <div className="r-usage-progress-wrap">
            <ProgressBar
              usedPct={usedPct}
              projPct={projPct}
              overQuota={overQuota}
            />
          </div>
        )}
        <div className="r-usage-status-line">
          <span className="r-usage-pct">
            {hasUnlimitedQuota ? "No monthly limit" : `${usedPct}% used`}
          </span>
          <span className="r-usage-status-row">
            <span className={classNames("r-usage-status-dot", `r-usage-status-dot--${statusTone}`)} />
            <span className={classNames("r-usage-status-label", `r-usage-status-label--${statusTone}`)}>
              {statusLabel}
            </span>
          </span>
        </div>
      </div>

      <Divider />

      {/* Breakdown rows */}
      <div className="r-usage-breakdown">
        <DotRow tone="current" label="Current views" value={fmt(currentViews)} />
        <DotRow
          tone="projected"
          label="Projected"
          value={fmt(projected)}
          hint={`Estimated end-of-month total based on ${dayjs().date()} days elapsed`}
        />
        <DotRow tone="included" label="Included in plan" value={fmtQuota(quota)} />
      </div>

      {billingDate && (
        <>
          <Divider />
          <div className="r-usage-reset-block">
            <StatRow
              label="Resets on"
              value={formatter_mmdy.format(getStartOfNextMonth(new Date()))}
            />
          </div>
        </>
      )}
    </div>
  );
};

// ─── PlanUsageChart ───────────────────────────────────────────────────────────

/**
 * @typedef {object} Plan
 * @property {string} name   Display name, e.g. "Standard".
 * @property {number} quota  Monthly view limit.
 * @property {number} price  Monthly price in USD.
 */

/**
 * Standalone monthly plan usage breakdown component.
 *
 * Renders a 12-month usage chart from container-provided data, derives a plan
 * recommendation via {@link computeRecommendation}, and shows an account-health
 * sidebar. Shows {@link PlanUsageChartSkeleton} while loading and an inline
 * error message on failure.
 *
 * @param {object}      props
 * @param {Plan}        props.currentPlan    Current org plan — from Redux store.
 * @param {Plan[]}      [props.allPlans=[]]  All available plans sorted ascending
 *   by quota — used by the recommendation engine to suggest upgrade/downgrade targets.
 * @param {string|null} [props.billingDate=null]
 *   ISO 8601 date of the next quota reset.
 * @param {string}      [props.billingPath='/settings/billing']
 *   URL for the upgrade/downgrade CTA link.
 * @param {Array<{billing_month: string, widget_views: number}>|null} [props.history=null]
 *   Usage rows provided by the parent container.
 * @param {boolean}     [props.loading=false]
 *   Optional external loading state, typically driven by Redux.
 * @param {string|null} [props.error=null]
 *   Optional external error state, typically driven by Redux.
 * @param {string}      [props.title='Plan usage']
 *   Heading displayed in the card header.
 * @returns {JSX.Element}
 *
 * @example
 * const { currentPlan, allPlans, billingDate } = useSelector(s => s.account);
 *
 * <PlanUsageChart
 *   currentPlan={currentPlan}
 *   allPlans={allPlans}
 *   billingDate={billingDate}
 *   billingPath="/settings/billing"
 * />
 */
const PlanUsageChart = ({
  currentPlan,
  allPlans = [],
  billingDate = null,
  billingPath = "/billing",
  history: usageHistory = null,
  loading = false,
  error = null,
  title = "Plan usage",
}) => {
  const raw = usageHistory;

  const thisMonth = dayjs().format("YYYY-MM");
  const currentRow = raw?.find((r) => r.billing_month === thisMonth);
  const currentViews = Number(currentRow?.widget_views) || 0;
  const daysElapsed = dayjs().date();
  const daysInMonth = dayjs().daysInMonth();
  const projected = Math.round(
    (currentViews / Math.max(daysElapsed, 1)) * daysInMonth
  );
  const quota = currentPlan?.quota ?? 0;
  const hasUnlimitedQuota = quota === -1;

  const maxVal = useMemo(
    () =>
      raw
        ? Math.max(
            ...raw.map((r) => Number(r.widget_views) || 0),
            hasUnlimitedQuota ? 0 : quota
          )
        : quota,
    [raw, quota, hasUnlimitedQuota]
  );

  const rec = useMemo(() => {
    if (!raw || !currentPlan) {
      return null;
    }
    return computeRecommendation({
      history: raw,
      currentViews,
      daysElapsed,
      daysInMonth,
      currentPlan,
      allPlans,
    });
  }, [raw, currentPlan, allPlans, currentViews, daysElapsed, daysInMonth]);

  const chartData = useMemo(() => buildChartData(raw, quota), [raw, quota]);
  const chartOptions = useMemo(() => buildChartOptions(maxVal), [maxVal]);

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return <PlanUsageChartSkeleton title={title} />;
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <Card>
        <Card.Header title={title} />
        <Card.Body>
          <div className="r-usage-error">Failed to load — {error}</div>
        </Card.Body>
      </Card>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Card>
      <Card.Header
        title={title}
        subtitle={
          currentPlan
            ? `${currentPlan.name} · ${fmtQuota(quota)} views/mo`
            : null
        }
      >
        {!hasUnlimitedQuota && (
          <div className="r-usage-quota-legend">
            <div className="r-usage-quota-legend-line" />
            <span className="r-usage-quota-legend-label">Monthly quota</span>
          </div>
        )}
      </Card.Header>
      <Card.Body>
        <div className="r-plan-usage-body">
          {/* Chart */}
          <div className="r-usage-chart-area">
            {chartData && (
              <Chart
                chartType="ColumnChart"
                data={chartData}
                width="100%"
                height="240px"
                options={chartOptions}
              />
            )}
          </div>

          {/* Sidebar */}
          {currentPlan && (
            <Sidebar
              currentViews={currentViews}
              quota={quota}
              projected={projected}
              billingDate={billingDate}
            />
          )}
        </div>

        {currentPlan && rec.type !== "downgrade" ? (
          <div className="r-usage-reco-row">
            <div className="r-usage-reco-col">
              <RecommendationPanel rec={rec} billingPath={billingPath} />
            </div>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
};

export default PlanUsageChart;
